import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import './ToastContext.scss';

type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * Global window-level handle to the toast system.
 *
 * Most callers should use `useToast()`. This escape hatch exists for code
 * that runs OUTSIDE React (e.g. the global `invalid` event listener in
 * `index.tsx` that suppresses the browser's English HTML5 validation
 * tooltip and replaces it with our Lithuanian toast). Wired up by
 * `ToastProvider` and torn down on unmount.
 */
declare global {
  interface Window {
    __gidasToast?: (message: string, type?: ToastType, duration?: number) => void;
  }
}

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  duration: number;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

let idCounter = 0;

const ICONS: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle size={20} />,
  error: <XCircle size={20} />,
  warning: <AlertTriangle size={20} />,
  info: <Info size={20} />,
};

const ToastItem: React.FC<{ toast: Toast; onRemove: (id: number) => void }> = ({ toast, onRemove }) => {
  React.useEffect(() => {
    const timer = setTimeout(() => onRemove(toast.id), toast.duration);
    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onRemove]);

  return (
    <div className={`toast-item toast-${toast.type}`}>
      <span className="toast-icon">{ICONS[toast.type]}</span>
      <span className="toast-message">{toast.message}</span>
      <button className="toast-close" onClick={() => onRemove(toast.id)}>
        <X size={16} />
      </button>
    </div>
  );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'info', duration = 4000) => {
    const id = ++idCounter;
    setToasts(prev => [...prev, { id, message, type, duration }]);
  }, []);

  // Expose a window-level bridge for non-React code (see `__gidasToast`
  // declaration above). Using a ref-style register/unregister keeps the
  // bridge in sync with the provider's lifecycle even with HMR / StrictMode
  // double-mounts.
  useEffect(() => {
    window.__gidasToast = showToast;
    return () => {
      if (window.__gidasToast === showToast) {
        delete window.__gidasToast;
      }
    };
  }, [showToast]);

  const portal = toasts.length > 0
    ? ReactDOM.createPortal(
        <div className="toast-container">
          {toasts.map(t => (
            <ToastItem key={t.id} toast={t} onRemove={removeToast} />
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {portal}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};
